import { Box, Stack, Typography, useTheme } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import ReactApexcharts from 'src/@core/components/react-apexcharts';
import buildFilterString from '../../helpers/buildFilterString';
import { useSettings } from 'src/@core/hooks/useSettings';
import router from 'next/router';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { useCallback, useMemo, useRef } from 'react';
import { useGetDashboardMetricsQuery } from 'src/queries/services/salesorder';
import useWindowSize from 'src/@core/hooks/useWindowResize';

const ConsignmentBreakdownWidget = ({ dashboardDateStrings = [[''], ['']], orderByValue = '' }) => {
  // Hooks
  const theme = useTheme();
  const settings = useSettings();
  const { t } = useVigoTranslation();
  const { width } = useWindowSize();

  const queryParamFilter = useMemo(() => {
    return buildFilterString(orderByValue, dashboardDateStrings);
  }, [orderByValue, dashboardDateStrings]);

  const { data: dashboardMetrics } = useGetDashboardMetricsQuery({ params: { filter: queryParamFilter } });

  const metrics = useMemo(() => {
    const total = dashboardMetrics?.total ?? 0;
    const clean = dashboardMetrics?.cleanDeliveries ?? 0;
    const outForDelivery = dashboardMetrics?.outForDelivery ?? 0;
    const discrepancies = dashboardMetrics?.discrepancies ?? 0;
    const awaitingDispatch = dashboardMetrics?.awaitingDespatch ?? 0;
    const bar = total > 0 ? 100 / total : 0;

    return {
      total,
      clean,
      outForDelivery,
      discrepancies,
      awaitingDispatch,
      series: [bar === 0 ? 0 : 100, clean * bar, outForDelivery * bar, discrepancies * bar, awaitingDispatch * bar]
    };
  }, [dashboardMetrics]);
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;

  const onClick = useCallback(
    (seriesIndex: number) => {
      let filter = '';

      switch (seriesIndex) {
        case 0:
          break;
        case 1:
          filter = "latestStatus eq 'Delivered'";
          break;
        case 2:
          filter = "latestStatus eq 'Out For Delivery'";
          break;
        case 3:
          filter = 'hasDiscrepancies eq true';
          break;
        case 4:
          filter =
            "latestStatus ne 'Out For Delivery' and latestStatus ne 'Delivered' and latestStatus ne 'Discrepancies'";
          break;
        default:
          filter = '';
          break;
      }

      if (!settings.settings.unlockDashboardLayout) {
        router.push(
          `/uiflow/consignment?filter=${buildFilterString(
            orderByValue,
            dashboardDateStrings
          )}&extrafilter=isDeleted eq false` + (filter.length > 0 ? ` and ${filter}` : '')
        );
      }
    },
    [settings.settings.unlockDashboardLayout, orderByValue, dashboardDateStrings]
  );
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  /**
   * Chart options
   */
  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        height: 390,
        type: 'radialBar',
        events: {
          dataPointSelection: function (chartContext, seriesIndex, config) {
            onClickRef.current(config.dataPointIndex);
          },
          legendClick: function (chartContext, seriesIndex, _config) {
            onClickRef.current(seriesIndex ?? 0);
          }
        }
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '20%',
            background: 'transparent',
            image: undefined
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              formatter: function (val) {
                const newval = val < 100 ? (Math.round(val * 100) / 100).toFixed(2) : val;

                return newval + '%';
              },
              show: true
            }
          }
        }
      },
      labels: ['Total Consignments', 'Clean Deliveries', 'Out for Delivery', 'Discrepancies', 'Awaiting Dispatch'],
      legend: {
        show: true,
        floating: true,
        fontSize: '11px',
        fontWeight: 'bold',
        position: 'left',
        offsetX: width <= 1300 ? -10 : 50,
        offsetY: -5,
        labels: {
          useSeriesColors: theme.palette.mode !== 'light'
        },
        formatter: function (seriesName) {
          switch (seriesName) {
            case 'Total Consignments':
              return t(seriesName) + ' ' + metricsRef.current.total;
            case 'Clean Deliveries':
              return t(seriesName) + ' ' + metricsRef.current.clean;
            case 'Out for Delivery':
              return t(seriesName) + ' ' + metricsRef.current.outForDelivery;
            case 'Discrepancies':
              return t(seriesName) + ' ' + metricsRef.current.discrepancies;
            case 'Awaiting Dispatch':
              return t(seriesName) + ' ' + metricsRef.current.awaitingDispatch;
            default:
              return t(seriesName);
          }
        },
        itemMargin: {
          vertical: 1
        }
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              show: false
            }
          }
        }
      ]
    }),
    [theme.palette.mode, t, width]
  );

  return (
    <Stack
      direction="column"
      sx={{
        height: '100%',
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e',
        borderRadius: '5px',
        cursor: 'pointer'
      }}>
      <Box>
        <Typography
          sx={{ textAlign: 'left', marginTop: '10px', marginLeft: '10px', fontSize: '1.5rem', fontWeight: '700' }}>
          {t('Consignment Breakdown')}
        </Typography>
      </Box>

      <ReactApexcharts options={options} series={metrics.series} type="radialBar" height="100%" />
    </Stack>
  );
};

export default ConsignmentBreakdownWidget;
